<template>
  <SoWidget :loading="loading" style="border-radius: 0 !important" no-padding>
    <div class="d-flex">
      <div
        v-if="newsFeed.type === newFeedType.INCIDENT"
        :class="`bg-critical pl-3`"
      />
      <div class="w-100 pa-4">
        <SoRow class="ma-0 justify-space-between align-center">
          <SoBadge :color="badgeColor">
            {{ daysAgoFormatted(newsFeed.publishDate) }}
          </SoBadge>
          <SoIcon :icon="iconType" :color="iconColor" />
        </SoRow>
        <slot name="title">
          <h3
            class="font-weight-bold py-3 border-b-sm"
            data-testid="news-feed-title"
          >
            {{ newsFeed.title }}
          </h3>
        </slot>
        <div :class="showMore ? 'mb-0 feed-limit' : 'mb-3 feed'" class="mt-3">
          <slot name="text">
            <div
              :ref="'feed-body-' + id"
              data-testid="news-feed-description"
              class="text-body-1 bulletpoint-list"
              v-html="formattedHtml"
            />
          </slot>
        </div>
        <SoButton
          v-if="isLimitedHeight"
          full-width
          :title="showMore ? 'show more' : 'show less'"
          :icon="showMore ? 'chevron-down' : 'chevron-up'"
          class="text-center pa-3"
          type="text"
          data-testid="news-feed-show-more"
          @click="toggleShowMore()"
        />
        <div class="text-body-2 py-3 border-t-sm">
          <em data-testid="news-feed-published-date"
            >Published on: {{ formattedDate }}
          </em>
        </div>
      </div>
    </div>
  </SoWidget>
</template>

<script lang="ts">
  import { marked } from "marked"
  import { defineComponent, PropType } from "vue"
  import { daysAgoFormatted, formatDate } from "@/helpers/dateHelper"
  import { NewFeedType, NewFeedTypeIcon } from "@/types/NewFeedType"
  import SoWidget from "@/components/SoWidget.vue"
  import SoBadge from "@/components/SoBadge.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoRow from "@/components/SoRow.vue"
  import { NewsfeedService } from "@/services/NewsfeedService"
  import { Newsfeed } from "@/models/newsfeed/newsfeed"
  import { INewsfeedItem } from "@/models/newsfeed/newsfeed.types"
  import { NEWSFEED_BLOCK_MAX_HEIGHT } from "@/constants/general"

  export default defineComponent({
    components: {
      SoRow,
      SoButton,
      SoIcon,
      SoBadge,
      SoWidget,
    },
    props: {
      id: {
        type: Number,
        required: true,
        default: 1,
      },
      newsFeed: {
        type: Object as PropType<INewsfeedItem>,
        default: () => Newsfeed,
      },
    },
    data() {
      return {
        articlesQuery: NewsfeedService.getPublishedArticles(),
        daysAgoFormatted: daysAgoFormatted,
        showMore: false,
        bodyHeight: 0,
      }
    },
    computed: {
      loading() {
        return this.articlesQuery?.isFetching ?? false
      },
      newFeedType() {
        return NewFeedType
      },
      days() {
        return this.newsFeed.publishDate.fromNow()
      },
      formattedDate() {
        return formatDate(this.newsFeed.publishDate)
      },
      badgeColor() {
        const today = new Date()
        const publishDate = this.newsFeed.publishDate.toDate()

        today.setHours(0, 0, 0, 0)
        publishDate.setHours(0, 0, 0, 0)

        if (publishDate.getTime() === today.getTime()) {
          return "critical"
        } else {
          return "neutral"
        }
      },
      iconType() {
        switch (this.newsFeed.type) {
          case NewFeedType.BUG:
            return NewFeedTypeIcon.BUG_ICON
          case NewFeedType.INCIDENT:
            return NewFeedTypeIcon.INCIDENT_ICON
          case NewFeedType.PROCESS:
            return NewFeedTypeIcon.PROCESS_ICON
          case NewFeedType.INFO:
            return NewFeedTypeIcon.INFO_ICON
          case NewFeedType.OTHER:
            return NewFeedTypeIcon.DEFAULT_ICON
          default:
            return NewFeedTypeIcon.DEFAULT_ICON
        }
      },
      iconColor() {
        switch (this.newsFeed.type) {
          case NewFeedType.BUG:
            return "critical"
          case NewFeedType.INCIDENT:
            return "critical"
          case NewFeedType.PROCESS:
            return "normal"
          case NewFeedType.INFO:
            return "neutral"
          case NewFeedType.OTHER:
            return "default"
          default:
            return "default"
        }
      },
      formattedHtml() {
        let string = this.newsFeed.body
        const tokens = marked.lexer(string)

        string = marked.parser(tokens)

        return string ?? "</div>"
      },
      isLimitedHeight() {
        return this.bodyHeight >= NEWSFEED_BLOCK_MAX_HEIGHT
      },
    },
    mounted() {
      this.$nextTick(() => this.getBodyHeight())
      this.$nextTick(() => this.isShowMore())
    },
    updated() {
      this.$nextTick(() => this.getBodyHeight())
      this.$nextTick(() => this.isShowMore())
    },
    methods: {
      isShowMore() {
        if (this.bodyHeight >= NEWSFEED_BLOCK_MAX_HEIGHT) {
          this.showMore = true
        }
      },
      toggleShowMore() {
        this.showMore = !this.showMore
      },
      getBodyHeight() {
        const element = this.$refs["feed-body-" + this.id] as HTMLElement
        this.bodyHeight = element.clientHeight

        return this.bodyHeight ?? 0
      },
    },
  })
</script>
<style lang="css" scoped>
  :deep(.so-widget-text hr) {
    display: block;
    margin: 5px 0;
    border-width: 0;
    height: 2px;
  }
  :deep(a) {
    text-decoration: none;
    color: rgb(var(--v-theme-primary));
  }
  :deep(img) {
    max-width: 100%;
    height: auto;
  }
  .feed-limit {
    overflow: hidden;
    max-height: 450px;
    transition: all 0.2s linear;
  }
  .feed {
    overflow: visible;
    max-height: initial;
    transition: all 0.2s linear;
  }
</style>
